import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import UMAPVisualization from "./components/UMAPVisualization";
import { projectWithUMAPCentered } from "../../services/umapProjection";

interface ClassificationScore {
  id: string;
  classification: string;
  parameter: string;
  userAverage: number;
  globalAverage: number;
  difference: number;
}

interface UMAPPoint {
  userId: string;
  x: number;
  y: number;
  userName?: string;
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [scores, setScores] = useState<ClassificationScore[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [overallSimilarity, setOverallSimilarity] = useState(0);
  const [umapPoints, setUmapPoints] = useState<UMAPPoint[]>([]);
  const [loadingUMAP, setLoadingUMAP] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;

    setLoadingData(true);
    try {
      // Obtener todas las clasificaciones
      const { data: classifications, error: classError } = await supabase
        .from("classifications")
        .select("*");

      if (classError) throw classError;

      // Obtener todas las preguntas con su clasificación
      const { data: questions, error: questionsError } = await supabase
        .from("questions")
        .select("id, classification_id, polarity");

      if (questionsError) throw questionsError;

      // Obtener respuestas del usuario
      const { data: userResponses, error: userError } = await supabase
        .from("user_responses")
        .select("question_id, score")
        .eq("user_id", user.id);

      if (userError) throw userError;

      // Obtener todas las respuestas (para promedio global)
      const { data: allResponses, error: allError } = await supabase
        .from("user_responses")
        .select("question_id, score");

      if (allError) throw allError;

      // Crear mapa de pregunta a clasificación y polaridad
      const questionMap = new Map(
        questions?.map((q) => [q.id, { classificationId: q.classification_id, polarity: q.polarity }])
      );

      // Calcular promedios por clasificación
      const classificationScores: ClassificationScore[] = [];

      classifications?.forEach((classification) => {
        // Filtrar preguntas de esta clasificación
        const classQuestionIds = questions
          ?.filter((q) => q.classification_id === classification.id)
          .map((q) => q.id) || [];

        if (classQuestionIds.length === 0) return;

        // Calcular promedio del usuario para esta clasificación
        const userScoresForClass = userResponses?.filter((r) =>
          classQuestionIds.includes(r.question_id)
        ) || [];

        // Ajustar scores según polaridad
        const adjustedUserScores = userScoresForClass.map((r) => {
          const questionInfo = questionMap.get(r.question_id);
          if (questionInfo?.polarity === "negative") {
            return 100 - r.score;
          }
          return r.score;
        });

        const userAverage =
          adjustedUserScores.length > 0
            ? adjustedUserScores.reduce((a, b) => a + b, 0) / adjustedUserScores.length
            : 0;

        // Calcular promedio global para esta clasificación
        const globalScoresForClass = allResponses?.filter((r) =>
          classQuestionIds.includes(r.question_id)
        ) || [];

        const adjustedGlobalScores = globalScoresForClass.map((r) => {
          const questionInfo = questionMap.get(r.question_id);
          if (questionInfo?.polarity === "negative") {
            return 100 - r.score;
          }
          return r.score;
        });

        const globalAverage =
          adjustedGlobalScores.length > 0
            ? adjustedGlobalScores.reduce((a, b) => a + b, 0) / adjustedGlobalScores.length
            : 50;

        const difference = Math.abs(userAverage - globalAverage);

        if (userScoresForClass.length > 0) {
          classificationScores.push({
            id: classification.id,
            classification: classification.classification,
            parameter: classification.parameter,
            userAverage: Math.round(userAverage),
            globalAverage: Math.round(globalAverage),
            difference: Math.round(difference),
          });
        }
      });

      // Ordenar por clasificación
      classificationScores.sort((a, b) => a.id.localeCompare(b.id));

      setScores(classificationScores);

      // Calcular similitud general (100 - promedio de diferencias)
      if (classificationScores.length > 0) {
        const avgDifference =
          classificationScores.reduce((sum, s) => sum + s.difference, 0) /
          classificationScores.length;
        setOverallSimilarity(Math.round(100 - avgDifference));
      }

      // Cargar datos para UMAP
      await loadUMAPData();
    } catch (error) {
      console.error("Error cargando datos:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const loadUMAPData = async () => {
    if (!user) return;
    
    setLoadingUMAP(true);
    try {
      // Obtener todas las respuestas de usuarios con al menos una respuesta
      const { data: allResponses, error: responsesError } = await supabase
        .from('user_responses')
        .select('user_id, question_id, score');

      if (responsesError) throw responsesError;

      if (!allResponses || allResponses.length === 0) {
        setLoadingUMAP(false);
        return;
      }

      // Obtener información de usuarios
      const uniqueUserIds = [...new Set(allResponses.map(r => r.user_id))];
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, username')
        .in('id', uniqueUserIds);

      if (usersError) throw usersError;

      // Crear un mapa de userId a username
      const userMap = new Map(usersData?.map(u => [u.id, u.username]) || []);

      // Obtener todas las preguntas con sus clasificaciones
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select('id, classification_id, polarity');

      if (questionsError) throw questionsError;

      // Obtener todas las clasificaciones
      const { data: classificationsData, error: classificationsError } = await supabase
        .from('classifications')
        .select('id, classification');

      if (classificationsError) throw classificationsError;

      // Crear mapa de pregunta a clasificación y polaridad
      const questionToClassification = new Map(
        questionsData?.map(q => [q.id, { classificationId: q.classification_id, polarity: q.polarity }]) || []
      );

      // Agrupar respuestas por usuario
      const userResponsesMap = new Map<string, Array<{ question_id: string; score: number }>>();
      
      allResponses.forEach(response => {
        if (!userResponsesMap.has(response.user_id)) {
          userResponsesMap.set(response.user_id, []);
        }
        userResponsesMap.get(response.user_id)!.push({
          question_id: response.question_id,
          score: response.score
        });
      });

      // Construir vectores para cada usuario
      const userVectors: Array<{
        userId: string;
        userName: string;
        vector: number[];
        subVectors: {
          v_P: number[];
          v_V: number[];
          v_L: number[];
          v_A: number[];
          v_C: number[];
        };
        metaDimensions: {
          plasticityIndex: number;
          stabilityIndex: number;
          sensoryFrictionIndex: number;
        };
      }> = [];

      userResponsesMap.forEach((responses, userId) => {
        // Agrupar respuestas por clasificación
        const classificationScores = new Map<string, number[]>();
        
        responses.forEach(response => {
          const questionInfo = questionToClassification.get(response.question_id);
          if (questionInfo) {
            const classificationId = questionInfo.classificationId;
            if (!classificationScores.has(classificationId)) {
              classificationScores.set(classificationId, []);
            }
            
            // Ajustar score según polaridad
            let adjustedScore = response.score;
            if (questionInfo.polarity === 'negative') {
              adjustedScore = 100 - response.score;
            }
            
            classificationScores.get(classificationId)!.push(adjustedScore);
          }
        });

        // Calcular promedio por clasificación (85 dimensiones)
        const vector: number[] = [];
        const allClassifications = [...new Set(questionsData?.map(q => q.classification_id) || [])].sort();
        
        // Construir sub-vectores por módulo
        const subVectors = {
          v_P: [] as number[],
          v_V: [] as number[],
          v_L: [] as number[],
          v_A: [] as number[],
          v_C: [] as number[]
        };
        
        allClassifications.forEach(classId => {
          const scores = classificationScores.get(classId) || [];
          const avg = scores.length > 0 
            ? scores.reduce((sum, val) => sum + val, 0) / scores.length 
            : 0;
          vector.push(avg);
          
          // Clasificar en sub-vectores según el prefijo
          if (classId.startsWith('P')) {
            subVectors.v_P.push(avg);
          } else if (classId.startsWith('V')) {
            subVectors.v_V.push(avg);
          } else if (classId.startsWith('L')) {
            subVectors.v_L.push(avg);
          } else if (classId.startsWith('A')) {
            subVectors.v_A.push(avg);
          } else if (classId.startsWith('C')) {
            subVectors.v_C.push(avg);
          }
        });

        // Calcular meta-dimensiones
        const avgP = subVectors.v_P.length > 0 
          ? subVectors.v_P.reduce((a, b) => a + b, 0) / subVectors.v_P.length 
          : 0;
        const avgV = subVectors.v_V.length > 0 
          ? subVectors.v_V.reduce((a, b) => a + b, 0) / subVectors.v_V.length 
          : 0;
        const avgL = subVectors.v_L.length > 0 
          ? subVectors.v_L.reduce((a, b) => a + b, 0) / subVectors.v_L.length 
          : 0;
        const avgA = subVectors.v_A.length > 0 
          ? subVectors.v_A.reduce((a, b) => a + b, 0) / subVectors.v_A.length 
          : 0;

        const metaDimensions = {
          plasticityIndex: (avgP + avgV + avgL) / 3,
          stabilityIndex: (avgP + avgV + avgL) / 3, // Ajustar según las clasificaciones reales
          sensoryFrictionIndex: avgA
        };

        // Solo incluir usuarios con al menos algunas respuestas
        if (vector.some(v => v > 0)) {
          userVectors.push({
            userId,
            userName: userMap.get(userId) || 'Usuario',
            vector,
            subVectors,
            metaDimensions
          });
        }
      });

      // Proyectar con UMAP si hay suficientes usuarios
      if (userVectors.length >= 2) {
        const projectedPoints = await projectWithUMAPCentered(userVectors, user!.id);
        
        const pointsWithNames = projectedPoints.map((point) => {
          const userVector = userVectors.find(uv => uv.userId === point.userId);
          return {
            ...point,
            userName: userVector?.userName || 'Usuario'
          };
        });
        
        setUmapPoints(pointsWithNames);
      }
    } catch (error) {
      console.error("Error cargando datos UMAP:", error);
    } finally {
      setLoadingUMAP(false);
    }
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando resultados...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <div className="w-full bg-gradient-to-r from-purple-500 to-pink-500 py-4 px-4 flex items-center gap-3 shadow-lg sticky top-0 z-10">
        <button
          onClick={() => navigate("/")}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <h1 className="text-white font-bold text-lg flex-1">Dashboard de Resultados</h1>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4 pb-8">
        {/* Score de similitud general */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-6 text-center">
          <div className="relative w-32 h-32 mx-auto mb-4">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="#e5e7eb"
                strokeWidth="12"
                fill="none"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="url(#gradient)"
                strokeWidth="12"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${(overallSimilarity / 100) * 352} 352`}
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#a855f7" />
                  <stop offset="100%" stopColor="#ec4899" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-4xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                {overallSimilarity}%
              </span>
            </div>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-1">Similitud General</h2>
          <p className="text-gray-500 text-sm">
            Qué tan parecido eres al promedio de usuarios
          </p>
        </div>

        {/* Visualización UMAP */}
        <UMAPVisualization
          points={umapPoints}
          currentUserId={user.id}
          loading={loadingUMAP}
        />

        {/* Leyenda */}
        <div className="flex items-center justify-center gap-6 px-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
            <span className="text-xs text-gray-600">Tu promedio</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-300"></div>
            <span className="text-xs text-gray-600">Promedio global</span>
          </div>
        </div>

        {/* Lista de clasificaciones */}
        {scores.length > 0 ? (
          <div className="space-y-3">
            {scores.map((score) => (
              <div
                key={score.id}
                className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-purple-500 bg-purple-50 px-2 py-0.5 rounded-full">
                        {score.id}
                      </span>
                      <span className="text-xs text-gray-400">{score.classification}</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">{score.parameter}</h3>
                  </div>
                  <div className="text-right">
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                        score.difference <= 10
                          ? "bg-green-100 text-green-600"
                          : score.difference <= 25
                          ? "bg-yellow-100 text-yellow-600"
                          : "bg-red-100 text-red-600"
                      }`}
                    >
                      {score.difference <= 10
                        ? "Muy similar"
                        : score.difference <= 25
                        ? "Similar"
                        : "Diferente"}
                    </span>
                  </div>
                </div>

                {/* Barra de comparación */}
                <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden">
                  {/* Marcador del promedio global */}
                  <div
                    className="absolute top-0 bottom-0 w-1 bg-gray-400 z-10"
                    style={{ left: `${score.globalAverage}%` }}
                  >
                    <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap">
                      {score.globalAverage}
                    </div>
                  </div>

                  {/* Barra del usuario */}
                  <div
                    className="absolute top-1 bottom-1 left-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                    style={{ width: `calc(${score.userAverage}% - 8px)` }}
                  ></div>

                  {/* Valor del usuario */}
                  <div
                    className="absolute top-1/2 transform -translate-y-1/2 text-xs font-bold text-white z-20"
                    style={{ left: `${Math.max(8, score.userAverage - 8)}%` }}
                  >
                    {score.userAverage}
                  </div>
                </div>

                <div className="flex justify-between mt-2 text-xs text-gray-400">
                  <span>0</span>
                  <span>50</span>
                  <span>100</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
              <i className="ri-bar-chart-box-line text-gray-400 text-3xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Sin datos aún</h3>
            <p className="text-gray-500 text-sm mb-4">
              Responde algunas preguntas para ver tus resultados aquí.
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-full"
            >
              Ir a responder
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
